import { useCallback, useEffect, useMemo, useState } from "react";
import { NavigateFunction } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { usePolling } from "@/hooks/usePolling";
import { buildPluginRows } from "@/lib/scrapingEvents";
import { WorkerLoadBarChart } from "../charts/WorkerLoadBarChart";
import { AnonAuthPieChart } from "../charts/AnonAuthPieChart";
import { InflightByWorkerBarChart } from "../charts/InflightByWorkerBarChart";
import {
  getScrapingByPlugin,
  ByPluginRow,
  WorkerInfo,
} from "@/services/scrapingStatsApi";
import {
  getInflightByWorker,
  InflightByWorkerResponse,
} from "@/services/workerApi";

// Répartition = qui traite quoi. Charge historique par worker (by-plugin),
// split anonyme/authentifié, et in-flight LIVE par worker (Tier 2).
export function DistributionTab({ live, navigate }: { live: boolean; navigate: NavigateFunction }) {
  const [days, setDays] = useState(30);
  const [pluginData, setPluginData] = useState<ByPluginRow[]>([]);
  const [workersMap, setWorkersMap] = useState<Record<string, WorkerInfo>>({});
  const [loadingHist, setLoadingHist] = useState(true);

  const [inflight, setInflight] = useState<InflightByWorkerResponse | null>(null);
  const [loadingInflight, setLoadingInflight] = useState(true);
  const [inflightError, setInflightError] = useState(false);

  // Historique (Postgres, non temps réel) : chargé au montage / changement de période.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingHist(true);
      try {
        const r = await getScrapingByPlugin(navigate, { days });
        if (!cancelled) {
          setPluginData(r.data);
          setWorkersMap(r.workers ?? {});
        }
      } catch {
        // non bloquant
      } finally {
        if (!cancelled) setLoadingHist(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days, navigate]);

  // In-flight (Redis, temps réel) : pollé tant que l'onglet est ouvert et Live actif.
  const fetchInflight = useCallback(async () => {
    try {
      const r = await getInflightByWorker(navigate);
      setInflight(r);
      setInflightError(false);
    } catch {
      // non bloquant : on garde la dernière photo connue, mais on le signale
      setInflightError(true);
    } finally {
      setLoadingInflight(false);
    }
  }, [navigate]);

  usePolling(fetchInflight, 5000, { enabled: live });

  const pluginRows = useMemo(() => buildPluginRows(pluginData), [pluginData]);

  const { anon, auth } = useMemo(() => {
    let anon = 0;
    let auth = 0;
    for (const row of pluginRows) {
      const info = workersMap[row.worker_id];
      if (info?.is_anonymous) anon += row.total;
      else auth += row.total;
    }
    return { anon, auth };
  }, [pluginRows, workersMap]);

  const orphanLocks = useMemo(() => {
    if (!inflight) return 0;
    const now = Date.now() / 1000;
    return inflight.workers.filter((w) => w.nearest_deadline != null && w.nearest_deadline < now).length;
  }, [inflight]);

  const infoFor = useCallback((id: string) => workersMap[id], [workersMap]);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <TooltipProvider delayDuration={150}>
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium">Charge par worker (historique)</CardTitle>
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="h-8 w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 derniers jours</SelectItem>
                  <SelectItem value="30">30 derniers jours</SelectItem>
                  <SelectItem value="90">90 derniers jours</SelectItem>
                  <SelectItem value="365">12 derniers mois</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <WorkerLoadBarChart rows={pluginRows} workersMap={workersMap} loading={loadingHist} />
            </CardContent>
          </Card>
        </TooltipProvider>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Flotte anonyme vs authentifiée</CardTitle>
          </CardHeader>
          <CardContent>
            <AnonAuthPieChart anonymous={anon} authenticated={auth} loading={loadingHist} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium">Tâches en cours par worker (temps réel)</CardTitle>
          <div className="flex items-center gap-3">
            {inflightError && (
              <span className="text-xs text-destructive">Données indisponibles</span>
            )}
            {orphanLocks > 0 && (
              <span className="flex items-center gap-1 text-xs text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                {orphanLocks} verrou(s) expiré(s) — requeue par le reaper sous peu
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <InflightByWorkerBarChart
            workers={inflight?.workers ?? []}
            infoFor={infoFor}
            loading={loadingInflight && inflight === null}
          />
        </CardContent>
      </Card>
    </div>
  );
}
